import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getSecondHandItem, deleteSecondHandItem } from '../../api';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { STATUS_META } from '../../components/common/SecondHandCard';
import {
  FiShoppingCart, FiArrowLeft, FiZap, FiMapPin, FiUser, FiEdit2, FiTrash2,
  FiClock, FiAlertCircle, FiTag,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import '../ProductDetail.css';
import './SecondHandDetail.css';

const NPR = (n) => 'NPR ' + parseFloat(n || 0).toLocaleString('en-NP');

const SecondHandDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addSecondHandItem } = useCart();
  const { user, isAdmin } = useAuth();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setLoading(true);
    getSecondHandItem(slug)
      .then(({ data }) => setItem(data))
      .catch(() => {
        toast.error('Listing not found');
        navigate('/second-hand');
      })
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  const handleAddToCart = async () => {
    setAdding(true);
    try {
      await addSecondHandItem(item);
      toast.success('Added to cart!');
    } catch (err) {
      toast.error(err.response?.data?.[0] || 'Error adding to cart');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this listing? This cannot be undone.')) return;
    try {
      await deleteSecondHandItem(item.slug);
      toast.success('Listing deleted');
      navigate('/dashboard?tab=listings');
    } catch {
      toast.error('Could not delete listing');
    }
  };

  if (loading) return (
    <div className="pd-page container">
      <div className="pd-skeleton">
        <div className="skeleton" style={{ height: 500 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="skeleton" style={{ height: 40 }} />
          <div className="skeleton" style={{ height: 24, width: '60%' }} />
          <div className="skeleton" style={{ height: 80 }} />
        </div>
      </div>
    </div>
  );
  if (!item) return null;

  const images = item.images || [];
  const currentImg = images[activeImg];
  const isOwner = item.is_mine;
  const isLive = item.status === 'approved' && !item.is_sold;
  const status = STATUS_META[item.status];
  const discount =
    item.original_price && parseFloat(item.original_price) > parseFloat(item.price)
      ? Math.round((1 - parseFloat(item.price) / parseFloat(item.original_price)) * 100)
      : null;

  const specs = [
    { label: 'Condition', value: item.condition_display },
    { label: 'Usage', value: item.usage_details },
    { label: 'Bought in', value: item.purchase_year },
    { label: 'Warranty left', value: item.warranty_remaining },
    { label: 'Location', value: item.location },
    { label: 'Category', value: item.category_name },
  ].filter((s) => s.value);

  return (
    <div className="pd-page shd-page">
      <div className="container">
        <button className="btn btn-ghost back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>

        {/* Owner / moderation banners */}
        {isOwner && item.status === 'pending' && (
          <div className="shd-banner pending">
            <FiClock />
            <div>
              <strong>Waiting for admin approval</strong>
              <span>Only you can see this listing right now. We will publish it once it is reviewed.</span>
            </div>
          </div>
        )}
        {isOwner && item.status === 'rejected' && (
          <div className="shd-banner rejected">
            <FiAlertCircle />
            <div>
              <strong>This listing was rejected</strong>
              <span>{item.rejection_reason || 'Contact support for details, or edit and resubmit it.'}</span>
            </div>
          </div>
        )}
        {item.is_sold && (
          <div className="shd-banner sold">
            <FiTag />
            <div><strong>Sold</strong><span>This item has already found a new owner.</span></div>
          </div>
        )}

        <div className="pd-layout">
          {/* Gallery */}
          <div className="pd-gallery">
            <div className="pd-main-img">
              {currentImg ? (
                <img src={currentImg.image} alt={currentImg.alt || item.title} />
              ) : (
                <div className="pd-no-img"><FiZap /></div>
              )}
            </div>
            {images.length > 1 && (
              <div className="pd-thumbs">
                {images.map((img, i) => (
                  <button
                    key={img.id}
                    className={`pd-thumb ${activeImg === i ? 'active' : ''}`}
                    onClick={() => setActiveImg(i)}
                  >
                    <img src={img.image} alt={img.alt || ''} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="pd-info">
            <div className="shd-tags">
              <span className="shd-used-tag">Pre-owned</span>
              <span className="shd-condition">{item.condition_display}</span>
              {(isOwner || isAdmin) && status && (
                <span className={`badge ${status.badge}`}>{status.icon} {status.label}</span>
              )}
            </div>

            <h1 className="pd-title">{item.title}</h1>

            <div className="shd-price-row">
              <div className="pd-price">{NPR(item.price)}</div>
              {item.original_price && (
                <>
                  <span className="shd-original">{NPR(item.original_price)}</span>
                  {discount && <span className="shd-save">Save {discount}%</span>}
                </>
              )}
            </div>

            {item.related_product_slug && (
              <p className="shd-related">
                Based on{' '}
                <Link to={`/products/${item.related_product_slug}`}>{item.related_product_title}</Link>
                {' '}— see the brand-new version
              </p>
            )}

            {item.description && <p className="pd-description">{item.description}</p>}

            {specs.length > 0 && (
              <div className="pd-specs-grid">
                {specs.map((s) => (
                  <div key={s.label} className="pd-spec">
                    <span className="spec-label">{s.label}</span>
                    <span className="spec-value">{s.value}</span>
                  </div>
                ))}
              </div>
            )}

            {item.accessories && (
              <div className="shd-accessories">
                <h3>What's included</h3>
                <p>{item.accessories}</p>
              </div>
            )}

            <div className="shd-seller">
              <div className="shd-seller-avatar">
                {(item.seller?.display_name || 'U')[0].toUpperCase()}
              </div>
              <div>
                <strong>{item.seller?.display_name}</strong>
                <span className="text-muted">
                  Seller{item.location ? ` · ${item.location}` : ''}
                </span>
              </div>
              {item.contact_phone && (isOwner || isAdmin) && (
                <a href={`tel:${item.contact_phone}`} className="btn btn-outline btn-sm shd-seller-call">
                  {item.contact_phone}
                </a>
              )}
            </div>

            {/* Actions */}
            {isOwner ? (
              <div className="shd-owner-actions">
                {!item.is_sold && (
                  <Link to={`/second-hand/${item.slug}/edit`} className="btn btn-outline">
                    <FiEdit2 /> Edit Listing
                  </Link>
                )}
                <button className="btn btn-ghost shd-delete" onClick={handleDelete}>
                  <FiTrash2 /> Delete
                </button>
              </div>
            ) : (
              <div className="pd-actions">
                <button
                  className="btn btn-primary btn-lg pd-add-btn"
                  onClick={handleAddToCart}
                  disabled={!isLive || adding}
                >
                  <FiShoppingCart /> {item.is_sold ? 'Sold Out' : adding ? 'Adding...' : 'Add to Cart'}
                </button>
                {!user && isLive && (
                  <p className="text-muted shd-signin-hint">
                    <FiUser /> <Link to="/signin">Sign in</Link> to complete your purchase faster
                  </p>
                )}
              </div>
            )}

            <div className="shd-note">
              <FiMapPin /> Pre-owned items are sold as described by the seller and verified by our team before listing.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecondHandDetail;
